import { ensureSubscriptionActionAllowed } from '../utils/subscriptionActionGuard';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Tabs, message } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import API_CONFIG from '../config/api';
import { useSelection } from '../SelectionContext';
import { useCommercialTreasuryPermissions } from './useCommercialTreasuryPermissions';
import CommercialTreasuryWorkspace, { useCommercialTreasuryStyle } from './CommercialTreasuryWorkspace';
import LogisticsTreasuryAccounts from '../gestion-logistics/pages/LogisticsTreasuryAccounts';
import LogisticsTreasuryMovements from '../gestion-logistics/pages/LogisticsTreasuryMovements';
import LogisticsTreasuryTransfers from '../gestion-logistics/pages/LogisticsTreasuryTransfers';

const DEFAULT_FILTERS = { dateRange: null, paymentType: 'all', modeType: 'all', search: '' };
const EMPTY_TOTALS = {
  total_incoming: null, total_outgoing: null, total_count: null,
  currency_symbol: null, currency_count: 0, total_treasury_balance: null,
  treasury_account_count: null, treasury_currency_symbol: null,
  treasury_currency_count: 0, treasury_balance_as_of: null,
  treasury_accounts_visible: null, treasury_accounts: [],
};
const EXPORT_BATCH_SIZE = 1000;
const modeTypeLabels = { cash: 'Caisse', bank: 'Banque', mobile: 'Mobile' };

const formatCurrency = (value, symbol) => {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return '—';
  const formatted = new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(numericValue);
  const displaySymbol = symbol === 'CFA' ? 'FCFA' : symbol;
  return displaySymbol ? formatted + ' ' + displaySymbol : formatted;
};

const normalizeTotals = (raw, canViewAccounts) => ({
  total_incoming: Number(raw.total_incoming ?? 0),
  total_outgoing: Number(raw.total_outgoing ?? 0),
  total_count: Number(raw.total_count ?? 0),
  currency_symbol: raw.currency_symbol || null,
  currency_count: Number(raw.currency_count || 0),
  total_treasury_balance: raw.total_treasury_balance == null ? null : Number(raw.total_treasury_balance),
  treasury_account_count: Number(raw.treasury_account_count || 0),
  treasury_currency_symbol: raw.treasury_currency_symbol || null,
  treasury_currency_count: Number(raw.treasury_currency_count || 0),
  treasury_balance_as_of: raw.treasury_balance_as_of || null,
  treasury_accounts_visible: raw.treasury_accounts_visible == null
    ? null : Boolean(canViewAccounts && raw.treasury_accounts_visible),
  treasury_accounts: canViewAccounts && raw.treasury_accounts_visible === true && Array.isArray(raw.treasury_accounts)
    ? raw.treasury_accounts : [],
});

const requestError = (error, fallback) => error.response?.data?.message || error.message || fallback;
const isCanceled = (error, signal) => signal.aborted || axios.isCancel?.(error) || error.code === 'ERR_CANCELED';

const GestionTresorerie = () => {
  const { selectedCompany, selectedWarehouse } = useSelection();
  const permissions = useCommercialTreasuryPermissions();
  const workspaceStyle = useCommercialTreasuryStyle();
  const [activeView, setActiveView] = useState('flows');
  const [refreshKey, setRefreshKey] = useState(0);
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [searchValue, setSearchValue] = useState('');
  const selectionKey = JSON.stringify([selectedCompany, selectedWarehouse]);
  const [paginationState, setPagination] = useState({ page: 1, limit: 20, selectionKey });
  const pagination = {
    page: paginationState.selectionKey === selectionKey ? paginationState.page : 1,
    limit: paginationState.limit,
  };
  const [flowState, setFlowState] = useState({ key: null, rows: [], total: 0, loading: false, error: null });
  const [totalsState, setTotalsState] = useState({ key: null, values: EMPTY_TOTALS, loading: false, error: null });
  const [exportLoading, setExportLoading] = useState(false);
  const latestScope = useRef({});
  const exportRequest = useRef(null);
  const exportBusy = useRef(false);
  const mounted = useRef(true);

  const availableViews = [
    ...(permissions.canViewTreasury ? ['flows'] : []),
    ...(permissions.canViewTreasuryAccounts ? ['accounts'] : []),
    ...(permissions.canViewTreasuryMovements ? ['movements'] : []),
    ...(permissions.canCreateTreasuryTransfers ? ['transfers'] : []),
  ];
  const currentView = availableViews.includes(activeView) ? activeView : availableViews[0];
  const baseParams = useMemo(() => {
    if (!selectedCompany || !selectedWarehouse) return null;
    const params = { company_id: selectedCompany, warehouse_id: selectedWarehouse };
    if (filters.dateRange?.[0] && filters.dateRange?.[1]) {
      params.start_date = filters.dateRange[0].format('YYYY-MM-DD');
      params.end_date = filters.dateRange[1].format('YYYY-MM-DD');
    }
    if (filters.paymentType !== 'all') params.payment_type = filters.paymentType;
    if (filters.modeType !== 'all') params.mode_type = filters.modeType;
    if (filters.search.trim()) params.search = filters.search.trim();
    return params;
  }, [filters, selectedCompany, selectedWarehouse]);
  const queryKey = JSON.stringify([baseParams, permissions.canViewTreasuryAccounts, refreshKey]);
  const pageKey = JSON.stringify([queryKey, pagination.page, pagination.limit]);
  const flowCurrent = flowState.key === pageKey;
  const totalsCurrent = totalsState.key === queryKey;
  const loading = Boolean(baseParams && currentView === 'flows' && (!flowCurrent || flowState.loading));
  const totalsLoading = Boolean(baseParams && currentView === 'flows' && (!totalsCurrent || totalsState.loading));

  // Invalidate responses/callbacks at render time, before effect cleanup on a scope change.
  latestScope.current = {
    queryKey, pageKey, canExport: permissions.canExportTreasury,
    canViewAccounts: permissions.canViewTreasuryAccounts,
    canTransfer: permissions.canCreateTreasuryTransfers,
    canViewFlows: permissions.canViewTreasury, currentView,
    pageReady: flowCurrent && !flowState.loading && !flowState.error,
  };

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; exportRequest.current?.abort(); };
  }, []);

  useEffect(() => {
    exportRequest.current?.abort();
  }, [queryKey, permissions.canExportTreasury]);

  useEffect(() => {
    if (searchValue.trim() === filters.search) return undefined;
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchValue.trim() }));
      setPagination((prev) => ({ ...prev, page: 1, selectionKey }));
    }, 350);
    return () => clearTimeout(timer);
  }, [searchValue, filters.search, selectionKey]);

  useEffect(() => {
    if (!baseParams || currentView !== 'flows' || !permissions.canViewTreasury) return undefined;
    const controller = new AbortController();
    setFlowState({ key: pageKey, rows: [], total: 0, loading: true, error: null });
    const load = async () => {
      try {
        const response = await axios.get(API_CONFIG.API_URL + '/treasury/flows', {
          params: { ...baseParams, page: pagination.page, limit: pagination.limit }, signal: controller.signal,
        });
        if (controller.signal.aborted || latestScope.current.pageKey !== pageKey) return;
        if (!response.data?.success) throw new Error(response.data?.message || 'Erreur de chargement des paiements.');
        setFlowState({ key: pageKey, rows: response.data.data || [],
          total: Number(response.data.pagination?.total || 0), loading: false, error: null });
      } catch (error) {
        if (isCanceled(error, controller.signal) || latestScope.current.pageKey !== pageKey) return;
        setFlowState({ key: pageKey, rows: [], total: 0, loading: false,
          error: requestError(error, 'Impossible de charger les paiements.') });
      }
    };
    load();
    return () => controller.abort();
  }, [baseParams, pageKey, pagination.page, pagination.limit, currentView, permissions.canViewTreasury]);

  useEffect(() => {
    if (!baseParams || currentView !== 'flows' || !permissions.canViewTreasury) return undefined;
    const controller = new AbortController();
    setTotalsState({ key: queryKey, values: EMPTY_TOTALS, loading: true, error: null });
    const load = async () => {
      try {
        const response = await axios.get(API_CONFIG.API_URL + '/treasury/flows/totals', {
          params: baseParams, signal: controller.signal,
        });
        if (controller.signal.aborted || latestScope.current.queryKey !== queryKey) return;
        if (!response.data?.success || !response.data.data) {
          throw new Error(response.data?.message || 'Erreur de chargement de la situation des comptes.');
        }
        setTotalsState({ key: queryKey,
          values: normalizeTotals(response.data.data, latestScope.current.canViewAccounts), loading: false, error: null });
      } catch (error) {
        if (isCanceled(error, controller.signal) || latestScope.current.queryKey !== queryKey) return;
        setTotalsState({ key: queryKey, values: EMPTY_TOTALS, loading: false,
          error: requestError(error, 'Impossible de charger la situation des comptes.') });
      }
    };
    load();
    return () => controller.abort();
  }, [baseParams, queryKey, currentView, permissions.canViewTreasury]);

  const handleFilterChange = (partial) => {
    setFilters((prev) => ({ ...prev, ...partial }));
    setPagination((prev) => ({ ...prev, page: 1, selectionKey }));
  };
  const handleSearchChange = (value) => {
    setSearchValue(value);
    if (!value) handleFilterChange({ search: '' });
  };
  const handleSearchSubmit = (value) => {
    setSearchValue(value);
    handleFilterChange({ search: value.trim() });
  };
  const handleResetFilters = () => {
    setSearchValue('');
    setFilters({ ...DEFAULT_FILTERS });
    setPagination((prev) => ({ ...prev, page: 1, selectionKey }));
  };
  const handlePageChange = (page, limit) => setPagination((prev) => ({
    page: Math.max(Number(page) || 1, 1),
    limit: [10, 20, 50, 100].includes(Number(limit)) ? Number(limit) : prev.limit,
    selectionKey,
  }));

  const handleExport = async (scope = 'page') => {
    const startedScope = latestScope.current;
    if (!startedScope.canExport || !startedScope.canViewFlows || startedScope.queryKey !== queryKey) return;
    if (exportBusy.current || !baseParams || !startedScope.pageReady) return;
    const controller = new AbortController();
    exportRequest.current = controller;
    exportBusy.current = true;
    setExportLoading(true);
    const messageKey = 'treasury-export';
    const stillCurrent = () => mounted.current && !controller.signal.aborted && latestScope.current.canExport
      && latestScope.current.canViewFlows && latestScope.current.queryKey === startedScope.queryKey
      && (scope !== 'page' || latestScope.current.pageKey === startedScope.pageKey);
    try {
      if (!(await ensureSubscriptionActionAllowed('data_export')) || !stillCurrent()) return;
      message.loading({ key: messageKey, content: scope === 'all' ? 'Export des résultats filtrés…' : 'Export de la page…' });
      let exportData = flowState.rows.slice();
      if (scope === 'all') {
        exportData = [];
        let page = 1;
        let limit = EXPORT_BATCH_SIZE;
        let expectedTotal = null;
        const seenIds = new Set();
        do {
          const response = await axios.get(API_CONFIG.API_URL + '/treasury/flows', {
            params: { ...baseParams, page, limit }, signal: controller.signal,
          });
          if (!stillCurrent()) return;
          if (!response.data?.success) throw new Error(response.data?.message || 'Export impossible.');
          const rows = response.data.data || [];
          const reportedTotal = response.data.pagination?.total;
          const total = Number(reportedTotal);
          if (reportedTotal == null || !Number.isInteger(total) || total < 0) {
            throw new Error('Impossible de vérifier la totalité des résultats. Relancez l’export.');
          }
          if (expectedTotal === null) expectedTotal = total;
          else if (total !== expectedTotal) throw new Error('Les données ont changé pendant l’export. Relancez l’export.');
          for (const row of rows) {
            if (row.id != null && seenIds.has(row.id)) throw new Error('Les données ont changé pendant l’export. Relancez l’export.');
            if (row.id != null) seenIds.add(row.id);
          }
          exportData.push(...rows);
          if (exportData.length > expectedTotal) {
            throw new Error('Les données ont changé pendant l’export. Relancez l’export.');
          }
          if (exportData.length === expectedTotal) break;
          if (!rows.length) throw new Error('Export incomplet : relancez l’export des résultats.');
          limit = Math.max(Number(response.data.pagination?.limit) || limit, 1);
          page += 1;
        } while (stillCurrent());
      }
      if (!stillCurrent()) return;
      if (!exportData.length) {
        message.warning({ key: messageKey, content: 'Aucune donnée à exporter.' });
        return;
      }
      const formatted = exportData.map((item) => ({
        'Date paiement': item.payment_date ? dayjs(item.payment_date).format('DD/MM/YYYY') : '',
        Type: item.payment_type === 'in' ? 'Encaissement' : 'Décaissement',
        'No paiement': item.payment_reference || '', Tiers: item.entity_name || '',
        'Document(s)': item.document_number || '', 'Type document': item.document_type || '',
        'Mode paiement': item.payment_mode_name || '',
        'Type mode': modeTypeLabels[item.payment_mode_type] || item.payment_mode_type || '',
        Montant: Number(item.amount || 0), Devise: item.currency_symbol || '',
        Notes: item.notes || '', 'Créé par': item.created_by_name || '',
      }));
      const worksheet = XLSX.utils.json_to_sheet(formatted);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Tresorerie');
      if (!stillCurrent()) return;
      XLSX.writeFile(workbook, 'Tresorerie_Commerciale_' + dayjs().format('YYYY-MM-DD_HH-mm') + '.xlsx');
      message.success({ key: messageKey, content: 'Export terminé avec succès.' });
    } catch (error) {
      if (!isCanceled(error, controller.signal) && stillCurrent()) {
        message.error({ key: messageKey, content: requestError(error, 'Erreur lors de l’export.') });
      }
    } finally {
      if (!stillCurrent()) message.destroy(messageKey);
      if (exportRequest.current === controller) exportRequest.current = null;
      exportBusy.current = false;
      if (mounted.current) setExportLoading(false);
    }
  };

  const viewAccounts = () => { if (latestScope.current.canViewAccounts) setActiveView('accounts'); };
  const viewTransfers = () => { if (latestScope.current.canTransfer) setActiveView('transfers'); };
  const retry = () => setRefreshKey((prev) => prev + 1);

  if (!selectedCompany || !selectedWarehouse) return <Alert type="info" showIcon message="Sélection requise"
    description="Sélectionnez une entreprise et un établissement pour afficher la trésorerie." />;
  if (!currentView) return <Alert type="warning" showIcon message="Accès à la trésorerie non autorisé" />;

  const tabs = [];
  if (permissions.canViewTreasury) tabs.push({ key: 'flows', label: 'Flux de paiements', children:
    <CommercialTreasuryWorkspace
      filters={filters} searchValue={searchValue} onSearchChange={handleSearchChange} onSearchSubmit={handleSearchSubmit}
      onFilterChange={handleFilterChange} onResetFilters={handleResetFilters}
      data={flowCurrent ? flowState.rows : []} totals={totalsCurrent ? totalsState.values : EMPTY_TOTALS}
      loading={loading} totalsLoading={totalsLoading}
      error={flowCurrent ? flowState.error : null} totalsError={totalsCurrent ? totalsState.error : null}
      pagination={{ ...pagination, total: flowCurrent ? flowState.total : 0 }} onPageChange={handlePageChange}
      canViewAccounts={permissions.canViewTreasuryAccounts} canExport={permissions.canExportTreasury}
      exportLoading={exportLoading} onExport={handleExport} onViewAccounts={viewAccounts}
      onRetry={retry} formatAmount={formatCurrency}
    /> });
  if (permissions.canViewTreasuryAccounts) tabs.push({ key: 'accounts', label: 'Comptes', children:
    <LogisticsTreasuryAccounts refreshKey={refreshKey} permissions={permissions} moduleScope="commercial" /> });
  if (permissions.canViewTreasuryMovements) tabs.push({ key: 'movements', label: 'Mouvements', children:
    <LogisticsTreasuryMovements active={currentView === 'movements'} refreshKey={refreshKey} permissions={permissions} moduleScope="commercial" /> });
  if (permissions.canCreateTreasuryTransfers) tabs.push({ key: 'transfers', label: 'Transferts', children:
    <LogisticsTreasuryTransfers permissions={permissions} onTransferCompleted={retry} moduleScope="commercial" /> });

  return <div className="commercial-treasury-page" style={workspaceStyle}>
    <header className="commercial-treasury-page__header">
      <div><h1>Trésorerie commerciale</h1><p>Vos comptes et vos paiements, dans une vue claire.</p></div>
      {permissions.canCreateTreasuryTransfers && <Button type="primary" icon={<SwapOutlined />} onClick={viewTransfers}>
        Effectuer un transfert
      </Button>}
    </header>
    <Tabs className="commercial-treasury-page__tabs" activeKey={currentView} onChange={setActiveView} items={tabs} />
  </div>;
};

export default GestionTresorerie;
